import React, { useEffect } from "react";
import { useLocalStore, useStoreState } from "easy-peasy";
import { Box, Link, Paper, TableRow, TableCell } from "@mui/material";
import SearchInput from "components/admin_v2/ui/SearchInput";
import BatchSelect from "components/admin_v2/common/BatchSelect";
import Spinner from "components/admin_v2/ui/Spinner";
import PaginatedTable from "components/admin_v2/ui/PaginatedTable";
import TopPageLayout from "components/admin_v2/nav/TopPageLayout";
import DistrictConfigTabs from "components/admin_v2/district_config/DistrictConfigTabs";
import RouterLink from "components/admin_v2/nav/RouterLink";
import GreenCheckbox from "components/admin_v2/ui/GreenCheckbox";
import { OutlinedBtn } from "components/admin_v2/ui/Buttons";
import { districtsStore } from "components/admin_v2/district_config/stores/districtsStore";
import { updateWithRouter, camelizeLocationSearch } from "lib/queryString";
import { pageRoutes } from "services/api";
import { batchDelete } from "services/apiDistricts";
import useSorting from "lib/useSorting";
import I18n from "utils/i18n.js";
import useDebounce from "lib/useDebounce";
import useCommonPageStyles from "components/admin_v2/common/useCommonPageStyles";

const Districts = (props) => {
  const isRestrictedAdmin = useStoreState((s) => s.app.isRestrictedAdmin);
  const cls = useCommonPageStyles();
  const store = useLocalStore(() => districtsStore(camelizeLocationSearch(props)));
  const [state, actions] = store;
  const searchInput = useDebounce(state.query);
  const updateQueryString = updateWithRouter(props);
  const { sortBy, sortableColumn } = useSorting({ field: "created_at", order: "desc" });

  useEffect(() => {
    actions.setFromRouter(camelizeLocationSearch(props));
  }, [props.location.search]);

  useEffect(() => {
    actions.fetchDistricts({ sortByField: sortBy.field, sortByOrder: sortBy.order });
  }, [searchInput, state.page, state.perPage, state.scope, sortBy, state.refresh]);

  useEffect(() => {
    updateQueryString({ page: 1, query: searchInput });
  }, [searchInput]);

  const actionsList = isRestrictedAdmin
    ? []
    : [{ label: I18n.t("ui.btn.delete"), action: batchDelete }];

  const updatePage = (_e, newPage) => updateQueryString({ page: newPage + 1 });

  const updatePerPage = (e) => {
    updateQueryString({ page: 1, per_page: e.target.value });
  };

  const onSearch = (e) => {
    actions.setQuery(e.target.value);
  };

  const headers = [
    {
      field: "batchCheck",
      label: (
        <GreenCheckbox
          checked={state.allSelected}
          onChange={(e) => actions.updateAllSelected(e.target.checked)}
        />
      ),
      skipSorting: true
    },
    { field: "id", label: "ID" },
    { field: "name", label: "District" },
    { field: "slug", label: "Slug" },
    { field: "schools", label: "Schools", skipSorting: true },
    { field: "hotline_email", label: "Hotline Email" },
    { field: "created_at", label: "Created" },
    { field: "", label: "" }
  ].map(sortableColumn);

  const row = (district) => (
    <TableRow hover key={`district-${district.id}`}>
      <TableCell component="th" style={{ width: "5%" }}>
        <GreenCheckbox
          checked={state.isSelected(district.id)}
          onChange={(e) => actions.updateSelectedIds({ isAdd: e.target.checked, id: district.id })}
        />
      </TableCell>
      <TableCell component="th" style={{ width: "5%" }}>
        <RouterLink to={pageRoutes.district(district.id)} className={cls.tableLink}>
          {district.id}
        </RouterLink>
      </TableCell>
      <TableCell style={{ width: "15%" }}>{district.name}</TableCell>
      <TableCell style={{ width: "10%" }}>{district.slug}</TableCell>
      <TableCell style={{ width: "25%" }}>
        {district.schools.map((s) => s.name).join(", ")}
      </TableCell>
      <TableCell style={{ width: "15%" }}>{district.hotlineEmail}</TableCell>
      <TableCell>{district.createdAt}</TableCell>
      <TableCell>
        <RouterLink to={pageRoutes.district(district.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.view")}
        </RouterLink>
        <RouterLink to={pageRoutes.districtEdit(district.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.edit")}
        </RouterLink>
        <Link
          onClick={() => actions.deleteDistrict(district.id)}
          className={cls.tableLink}
          underline="hover"
          disabled={isRestrictedAdmin}
        >
          {I18n.t("ui.btn.delete")}
        </Link>
      </TableCell>
    </TableRow>
  );

  return (
    <React.Fragment>
      <TopPageLayout title={I18n.t("district_config.title")} />
      <DistrictConfigTabs />
      <Box className={cls.btnsWrapper}>
        {!isRestrictedAdmin && (
          <RouterLink className={cls.actionBtn} to={pageRoutes.districtNew()}>
            <OutlinedBtn>{I18n.t("district_config.new_district.btn")}</OutlinedBtn>
          </RouterLink>
        )}
      </Box>
      <Paper className={cls.paper}>
        <Box className={cls.filters}>
          <BatchSelect store={store} actionsList={actionsList} />
          <SearchInput cls value={state.query} onChange={onSearch} />
        </Box>
        {state.loading ? (
          <Spinner />
        ) : (
          <PaginatedTable
            headers={headers}
            count={state.total}
            perPage={state.perPage}
            page={state.page}
            onPageChange={updatePage}
            onChangePerPage={updatePerPage}
          >
            {state.districts.map(row)}
          </PaginatedTable>
        )}
      </Paper>
    </React.Fragment>
  );
};

export default Districts;
